import {  Box, Grid, TextField, Typography  } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronDown } from "lucide-react";
import React from "react";
import { CommonWidgetContainer } from "../../components/Commons/CommonWidgetContainer.styles";
import {  FormValues, Errors  } from "../../routes/Profile/AddSkillBook/AddSkillBook.types";

interface CustomField {
  fieldId: number;
  fieldName: string;
  fieldValue: string;
}

interface CustomFieldsSectionProps {
  expanded: boolean;
  onToggle: () => void;
  formValues: FormValues;
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
  errors: Errors;
  t: (key: string) => string;
  readOnly?: boolean;
}

const CustomFieldsSection: React.FC<CustomFieldsSectionProps> = ({
  expanded,
  onToggle,
  formValues,
  setFormValues,
  errors,
  t,
  readOnly
}) => {
  const handleCustomFieldChange = (fieldId: number, fieldName: string, value: string) => {
    setFormValues((prev) => {
      const existingCustomFields = (prev.customFields as CustomField[]) || [];
      const fieldIndex = existingCustomFields.findIndex(
        (cf) => cf.fieldId === fieldId
      );

      let updatedCustomFields;
      if (fieldIndex >= 0) {
        updatedCustomFields = [...existingCustomFields];
        updatedCustomFields[fieldIndex] = {
          fieldId,
          fieldName,
          fieldValue: value
        };
      } else {
        updatedCustomFields = [
          ...existingCustomFields,
          {
            fieldId,
            fieldName,
            fieldValue: value
          }
        ];
      }

      return { ...prev, customFields: updatedCustomFields };
    });
  };

  const theme = useTheme();

  const customFields = (formValues.customFields as CustomField[]) || [];

  return (
    <CommonWidgetContainer
      sx={ {
        width: "100%",
        maxWidth: "100%",
        borderRadius: "12px",
        mb: 2,
        p: 2
      } }
    >
      <Box
        onClick={ onToggle }
        aria-pressed={ expanded }
        sx={ {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer"
        } }
      >
        <Typography sx={ {  fontWeight: "bold", color: theme.customColors.textPrimary, fontSize: "1.2rem"  } }>
          { t("custom_fields_section") }
        </Typography>
        <ChevronDown
          color={ theme.customColors.accentVariantA }
          width="2rem"
          height="2rem"
          style={ {
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform .4s linear",
            color: theme.customColors.borderAccent
          } }
        />
      </Box>

      { expanded && (
        <Grid container spacing={ 2 } sx={ { mt: 1 } }>
          { customFields.map((field) => (
            <Grid item xs={ 12 } sm={ 6 } key={ field.fieldId }>
              <p style={ { fontSize: "10px", margin: "0 0 4px 0" } }>{ field.fieldName }</p>
              <TextField
                fullWidth
                name={ `customField_${field.fieldId}` }
                label="-"
                value={ field.fieldValue || "" }
                onChange={ (e) => handleCustomFieldChange(field.fieldId, field.fieldName, e.target.value) }
                error={ !!errors[`customField_${field.fieldId}`] }
                helperText={ errors[`customField_${field.fieldId}`] }
                disabled={ readOnly }
                sx={ {
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: theme.customColors.textDisabledAlternative,
                    opacity: 1
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: theme.customColors.backgroundPrimary,
                    color: theme.customColors.textPrimary,
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: theme.customColors.border
                    }
                  },
                  "& .MuiInputLabel-root": {
                    color: theme.customColors.textPrimary,
                    backgroundColor: theme.customColors.backgroundPrimary,
                    borderRadius: "8px",
                    paddingX: 0.5
                  }
                } }
              />
            </Grid>
          )) }
        </Grid>
      ) }
    </CommonWidgetContainer>
  );
};

export default CustomFieldsSection;
